import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import * as btc from '@scure/btc-signer';
import { LottieStatusAnimation } from '@cbhq/cds-web/animation';
import { VStack } from '@cbhq/cds-web/layout';
import { useToast } from '@cbhq/cds-web/overlays/useToast';
import { TextBody, TextTitle3 } from '@cbhq/cds-web/typography';

import { AnalyticEvent } from ':fat/analytics/events';
import { logError, logGenericEvent, logRender } from ':fat/analytics/logActions';
import { logFundsAccessError } from ':fat/analytics/logMetrics';
import { broadcastTransaction } from ':fat/api/broadcastTransaction';
import { SingleColumnLayout } from ':fat/components/PageLayout/SingleColumnLayout';
import { Network } from ':fat/core/networks';
import {
  broadcastableBroadcasts,
  deleteOldBroadcasts,
  initBroadcast,
  updateBroadcast,
} from ':fat/database/queries';
import { errorToString } from ':fat/helpers/errors';
import { convertToFiat, useRates } from ':fat/hooks/useBalances';
import { reportError } from ':fat/init/bugsnag';
import { query } from ':fat/messaging/entities/databaseWorker/actions';
import { messages } from ':fat/pages/Broadcasting/BroadcastingPageMessages';
import { AppRoutes } from ':fat/routing/routes';
import {
  calculateDestinationValue,
  calculateInputValue,
  useTransactions,
} from ':fat/TransactionContext';
import { Transaction } from ':fat/workers/blockchain/types';

const MAX_BROADCAST_ATTEMPTS = 5;

let broadcastingStarted = false;

export function BroadcastingPage() {
  const navigate = useNavigate();
  const { fingerprint } = useParams();
  const { formatMessage } = useIntl();
  const toast = useToast();
  const { transactions } = useTransactions();
  const rates = useRates();

  const networksWithTransactions = Object.keys(transactions).filter(
    (network) => !!transactions[network as Network]?.length,
  );
  const anyTransactions =
    !!networksWithTransactions.length || !!Object.values(transactions).flat().length;

  useEffect(() => {
    logRender(AnalyticEvent.BroadcastingPageRendered);
  }, []);

  useEffect(() => {
    if (anyTransactions && rates) {
      const fp = parseInt(fingerprint ?? '');
      if (isNaN(fp)) {
        throw new Error('Invalid fingerprint');
      }

      if (!broadcastingStarted) {
        broadcastingStarted = true;
        void submitTransactions(
          fp,
          networksWithTransactions as Network[],
          transactions,
          rates,
        ).then(() => {
          navigate(AppRoutes.STATUS.replace(':fingerprint', fingerprint ?? ''));
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fingerprint, networksWithTransactions, anyTransactions, transactions, navigate, !!rates]);

  useEffect(() => {
    if (!anyTransactions) {
      logFundsAccessError('show_error_toast');
      toast.show(formatMessage(messages.noTransactionsErrorToast));
      navigate(AppRoutes.SECURITY_FRAME_LOADER);
    }
  }, [anyTransactions, navigate, toast, formatMessage]);

  return (
    <SingleColumnLayout>
      <VStack alignItems="center" justifyContent="center" spacing={4} maxWidth={450}>
        <LottieStatusAnimation height={250} status="loading" />
        <VStack gap={1}>
          <TextTitle3 as="h1" align="center">
            {formatMessage(messages.title)}
          </TextTitle3>
          <TextBody as="p" align="center" color="foregroundMuted">
            {formatMessage(messages.description)}
          </TextBody>
        </VStack>
      </VStack>
    </SingleColumnLayout>
  );
}

async function submitTransactions(
  fingerprint: number,
  networks: Network[],
  transactions: Record<Network, Transaction[]>,
  fiatRates: Record<Network, number>,
) {
  await query(deleteOldBroadcasts());

  for (const network of networks) {
    for (const tx of transactions[network]) {
      await query(
        initBroadcast({
          id: null,
          fingerprint,
          network,
          tx_hash: tx instanceof btc.Transaction ? tx.id : tx.hash!,
          hex: tx instanceof btc.Transaction ? tx.hex : tx.hex!,
          status: 'WAITING',
          attempts: 0,
          failure_reason: null,
          input_value: calculateInputValue(tx).toString(),
          dest_value: calculateDestinationValue(tx).toString(),
          confirmed: 0,
        }),
      );
    }
  }

  while (true) {
    const broadcasts = await query(broadcastableBroadcasts(fingerprint, MAX_BROADCAST_ATTEMPTS));
    if (!broadcasts.length) {
      break;
    }

    for (const broadcast of broadcasts) {
      if (broadcast.attempts >= MAX_BROADCAST_ATTEMPTS || broadcast.id === null) {
        continue;
      }

      await new Promise((res) => {
        setTimeout(res, 100);
      });

      try {
        const result = await broadcastTransaction(
          broadcast.network as Network,
          broadcast.hex,
          broadcast.xpubkey,
        );
        if (result.error_message || result.error_code !== 0 || result.status === 'FAILED') {
          throw new Error(`Broadcast error: ${result.error_message}, status ${result.error_code}`);
        }
        await query(updateBroadcast(broadcast.id, broadcast.attempts + 1, result.status));
        logGenericEvent(AnalyticEvent.WithdrawalBroadcastSuccess, {
          fiatValue: convertToFiat(
            broadcast.network as Network,
            BigInt(broadcast.input_value),
            fiatRates,
          ),
          cryptoValue: broadcast.input_value,
          network: broadcast.network,
          fingerprint: broadcast.fingerprint,
          xPub: broadcast.xpubkey,
        });
      } catch (err) {
        logError(AnalyticEvent.WithdrawalBroadcastFailure, {
          xpub: broadcast.xpubkey,
          txHashLength: broadcast.tx_hash.length,
          fingerprint: broadcast.fingerprint,
          network: broadcast.network,
          message: errorToString(err),
        });
        reportError(err as Error, {
          context: 'broadcastTransaction',
          metadata: [
            {
              name: 'details',
              data: {
                xpub: broadcast.xpubkey,
                txHashLength: broadcast.tx_hash.length,
                fingerprint: broadcast.fingerprint,
                network: broadcast.network,
                error: errorToString(err),
              },
            },
          ],
        });
        broadcastingStarted = false;
        await query(
          updateBroadcast(broadcast.id, broadcast.attempts + 1, 'FAILED', errorToString(err)),
        );
      }
    }
  }
}
